<template>
  <refonte-modal
    v-bind:show="props.show"
    v-on:close="close"
    title="Téléverser un document">
    <template v-slot:modalBody>
      <div
        v-if="loading"
        class="h-full w-full">
        <spinner
          sizes="w-20 h-20"
          thickness="border-8" />
      </div>
      <form
        v-else
        v-on:submit.prevent="submit">
        <div class="flex flex-col gap-[16px]">
          <div>
            <p class="mb-1 text-sm text-refonte_blue-200">Document concerné</p>
            <p
              class="rounded border px-3 py-2 font-semibold text-refonte_blue-200">
              {{ document?.name }}
            </p>
          </div>
          <div class="">
            <div
              v-if="!file"
              v-on:click="openFileDialog"
              class="group flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-refonte_blue-100 bg-refonte-white p-12 text-center transition-colors hover:border-refonte_blue-300">
              <p class="icon-upload text-refonte_blue-200"><IconUpload /></p>
              <p class="mb-1 text-sm font-medium text-refonte_blue-200">
                Cliquez pour sélectionner un fichier
              </p>
              <input
                type="file"
                class="hidden"
                ref="uploadInput"
                v-on:change="handleUploadFile"
                id="file-upload" />
            </div>
            <div v-else>
              <p class="mb-1">Fichier sélectionné :</p>
              <div
                class="bg-teal-50 flex items-center justify-between rounded border border-teal-100 p-3 text-sm text-teal-800">
                <div class="flex items-center gap-2">
                  <span
                    v-if="file"
                    class="max-w-[250px] truncate font-medium"
                    >{{ file.name }}</span
                  >
                </div>
                <button
                  class="text-teal-600 hover:text-teal-800"
                  v-on:click="deleteFile">
                  <font-awesome-icon
                    icon="trash"
                    size="1x" />
                </button>
              </div>
            </div>
          </div>
          <div v-if="document.hasExpirationDate">
            <refonte-datepicker
              v-on:input="updateDateExpiration"
              v-bind:min_date="new Date()"
              v-bind:disable_time_picker="true"
              class="rounded border border-refonte_blue-100 p-1 hover:border-refonte_blue-300"
              placeholder="Date de fin de validité (JJ/MM/YYYY)"
              v-bind:value="dateExpiration" />
          </div>
          <input-checkbox
            v-if="show_engagement"
            class="mb-0"
            v-on:input="updateValueDeclaration"
            label="Déclaration sur l'honneur<span class='text-jinius-red'>*</span> <br/>Je déclare sur l'honneur que cette attestation de vigilance date de moins de six mois." />
        </div>
      </form>
    </template>
    <template v-slot:modalFooter>
      <refonte-button
        variant="text"
        v-bind:on_click="close">
        Annuler
      </refonte-button>
      <refonte-button
        v-bind:on_click="submit"
        v-bind:disabled="
          !(
            file != undefined &&
            (!document.hasExpirationDate || dateExpiration != undefined) &&
            (!show_engagement || engagement)
          )
        ">
        Valider et Transmettre
      </refonte-button>
    </template>
  </refonte-modal>
</template>

<script setup lang="ts">
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import { onMounted, ref } from 'vue';
  import { API } from '@/http-common';
  import { ReferenceInterne } from '@/types/gestion/commandes-types';
  import { ClientDocument } from '@/types/formateur/client-documents';
  import IconUpload from '@/assets/img/refonte/icons/display/icon-upload.svg?component';
  import RefonteDatepicker from '@/components/refonte/inputs/refonte-datepicker.vue';
  import { format, Locale } from 'date-fns';
  import fr from 'date-fns/locale/fr';
  import inputCheckbox from '@/components/utils/input-checkbox.vue';

  const props = withDefaults(
    defineProps<{
      show: boolean;
      on_close: Function;
      on_submit?: Function;
      client_id: number;
      document: ClientDocument;
      class?: string;
      show_engagement?: boolean;
      url_prefix: string;
    }>(),
    {
      class: '',
      show_engagement: false
    }
  );

  const file = ref<File>();
  const dateExpiration = ref<Date>();
  const uploadInput = ref(null);
  const handleUploadFile = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input && input.files) {
      file.value = input.files[0];
    }
  };
  const deleteFile = () => {
    file.value = undefined;
  };

  const updateDateExpiration = (value) => {
    dateExpiration.value = value;
  };

  const detail = ref(null);
  const loading = ref<boolean>(false);

  const engagement = ref<boolean>(false);
  const updateValueDeclaration = (value) => {
    engagement.value = value;
  };
  const referenceInterneForm = ref<ReferenceInterne>({});

  const openFileDialog = () => {
    if (uploadInput.value) {
      uploadInput.value.click();
    }
  };

  const uploadFile = async () => {
    if (file.value) {
      const formData = new FormData();
      if (dateExpiration.value) {
        const dateExpirationToSend = format(
          dateExpiration.value.toString(),
          'dd/MM/yyyy',
          {
            locale: fr as unknown as Locale
          }
        );
        formData.append('dateExpiration', dateExpirationToSend);
      }

      formData.append('file', file.value);

      try {
        await API.post(
          `${props.url_prefix}/${props.client_id}/client-documents/${props.document.type}/file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'SKIP-REDIRECTION': true
            }
          }
        );
        if (props.on_submit) {
          props.on_submit();
        }
      } catch (e) {
        //TODO à voir
      }
    }
  };

  onMounted(async () => {
    file.value = undefined;
    dateExpiration.value = undefined;
  });

  function close() {
    detail.value = null;
    props.on_close();
    referenceInterneForm.value = {};
  }
  function submit(e: Event) {
    e.preventDefault();
    uploadFile();
    close();
  }
</script>

<style lang="scss" scoped>
  .modal-modifier-categorie {
    @apply bg-refonte_blue-100;
    padding: 8px;
    border-radius: 8px;
    margin-bottom: 16px;

    span {
      @apply text-refonte_blue-600;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }
  .modal-modifier-etablissement-table {
    :deep(.refonte-table-row) {
      @apply bg-refonte-pale-violet-2;
    }
  }

  .icon-upload {
    :deep(svg) {
      @apply text-refonte_blue-400;
      min-width: 16px;
      width: 16px;
      height: 16px;
    }
  }
</style>
